import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button, Card, Input, Select, Spinner, Badge } from "./ui";
import { ColumnFilter } from "./ColumnFilter";

type MapOp = "contains" | "equals" | "starts" | "anyOf";
const ANY_OF_SEP = "|";

/** How a proposal was arrived at. Ordered strongest first, and filterable, so you can accept all the
 *  certain ones in one go and look at the guesses separately. */
const KIND_LABEL: Record<string, string> = {
  exact: "zelfde waarde",
  format: "formaat",
  band: "valt in reeks",
  contains: "bevat",
  synonym: "tint van",
  combo: "combinatie",
  catchall: "verzamelvakje",
};

/** Mirrors sameThing() on the server — enough to flag "90 g" next to "90gr" before you create it. */
const SYNONYMS = [["blueangel", "blauerengel", "blaueengel", "blauwengel"], ["nordicswan", "nordicecolabel", "svanen"], ["euecolabel", "europeanecolabel"]];
function looksSame(a: string, b: string): boolean {
  const canon = (s: string) => {
    let t = s.trim().toLowerCase().replace(/g\s*\/\s*m²|g\s*\/\s*m2|gsm|gramm?|grs?\b|\bg\b/g, "g").replace(/[^a-z0-9]+/g, "");
    for (const g of SYNONYMS) if (g.includes(t)) t = g[0]!;
    return t;
  };
  return canon(a) === canon(b);
}
const OPS: { id: MapOp; label: string }[] = [
  { id: "equals", label: "is exact" },
  { id: "contains", label: "bevat" },
  { id: "starts", label: "begint met" },
  { id: "anyOf", label: "is één van" },
];

const emptyDraft = {
  id: undefined as number | undefined,
  pbsField: "",
  op: "equals" as MapOp,
  pbsValue: "",
  lsFilterId: 0,
  lsValueId: 0,
  note: "",
};

/**
 * PBS attribute → Lightspeed filter value. The shop filters on Kleur, Formaat, Gewicht, Materiaal,
 * Kenmerken and Eco Keurmerk; PBS has been shipping the same facts as free-form attributes all along.
 * This is where the two are tied together, seen, and corrected.
 */
export function AttrMapTab({ isAdmin, onFlash, onError }: { isAdmin: boolean; onFlash: (m: string) => void; onError: (e: unknown) => void }) {
  const utils = trpc.useUtils();
  const mappings = trpc.config.attrMap.list.useQuery();
  const lsValues = trpc.config.attrMap.lsValues.useQuery(undefined, { staleTime: 5 * 60_000 });
  const attrs = trpc.config.mainCat.attributes.useQuery(undefined, { staleTime: 5 * 60_000 });
  const suggest = trpc.config.attrMap.suggest.useQuery(undefined, { staleTime: 60_000 });
  const [countsOpen, setCountsOpen] = useState(false);
  const counts = trpc.config.attrMap.counts.useQuery(undefined, { enabled: countsOpen, staleTime: 60_000 });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [page, setPage] = useState(0);
  const preview = trpc.config.attrMap.preview.useQuery({ page, pageSize: 100, onlyMatched: true }, { enabled: previewOpen });

  const [draft, setDraft] = useState(emptyDraft);
  const [newValueTitle, setNewValueTitle] = useState("");
  const [newValueGroup, setNewValueGroup] = useState(0);
  /** Which PBS value this new shop value comes from, so the mapping is made in the same step. */
  const [newValueFrom, setNewValueFrom] = useState<{ field: string; value: string } | null>(null);
  const [unmappedField, setUnmappedField] = useState("");
  const [unmappedQ, setUnmappedQ] = useState("");
  const [selectedUnmapped, setSelectedUnmapped] = useState<string[]>([]);
  const unmapped = trpc.config.attrMap.unmapped.useQuery(undefined, { staleTime: 60_000 });

  const refresh = () => {
    utils.config.attrMap.list.invalidate();
    utils.config.attrMap.suggest.invalidate();
    utils.config.attrMap.counts.invalidate();
    utils.config.attrMap.preview.invalidate();
  };
  const upsert = trpc.config.attrMap.upsert.useMutation({ onSuccess: () => { refresh(); setDraft(emptyDraft); onFlash("Koppeling opgeslagen ✓"); }, onError });
  const del = trpc.config.attrMap.delete.useMutation({ onSuccess: () => { refresh(); onFlash("Koppeling verwijderd"); }, onError });
  const createValue = trpc.config.attrMap.createLsValue.useMutation({
    onSuccess: (v) => {
      utils.config.attrMap.lsValues.invalidate();
      utils.config.attrMap.unmapped.invalidate();
      refresh();
      setNewValueTitle(""); setNewValueFrom(null);
      onFlash(v.mapped ? `"${v.valueTitle}" toegevoegd aan ${v.filterTitle} én gekoppeld ✓` : `"${v.valueTitle}" toegevoegd aan ${v.filterTitle} ✓`);
    },
    onError,
  });

  // Suggestion list: per-column filters + a batch selection, because 88 proposals is not a list you
  // click through one button at a time.
  const [sugField, setSugField] = useState<string[]>([]);
  const [sugValue, setSugValue] = useState<string[]>([]);
  const [sugFilter, setSugFilter] = useState<string[]>([]);
  const [sugLsValue, setSugLsValue] = useState<string[]>([]);
  const [sugKind, setSugKind] = useState<string[]>([]);
  const [pickedSuggest, setPickedSuggest] = useState<string[]>([]);
  const acceptBulk = trpc.config.attrMap.upsertBulk.useMutation({
    onSuccess: (r) => { refresh(); setPickedSuggest([]); onFlash(r.failed ? `${r.created} gekoppeld, ${r.failed} mislukt` : `${r.created} koppeling(en) gemaakt ✓`); },
    onError,
  });

  // One product at a time: look at the block before 16.625 descriptions are rewritten.
  const [specCode, setSpecCode] = useState("");
  const specPreview = trpc.config.attrMap.specPreview.useQuery(
    { productCode: specCode.trim() },
    { enabled: specCode.trim().length > 2, staleTime: 30_000 },
  );
  const specApply = trpc.config.attrMap.specApply.useMutation({
    onSuccess: (r) => onFlash(`Omschrijving bijgewerkt op Lightspeed-product ${r.productId} ✓`),
    onError,
  });

  const [shopGroup, setShopGroup] = useState(0);
  const [renaming, setRenaming] = useState<{ valueId: number; title: string } | null>(null);
  const redundant = trpc.config.attrMap.redundant.useQuery(undefined, { staleTime: 60_000 });
  const cleanup = trpc.config.attrMap.cleanup.useMutation({
    onSuccess: (r) => {
      utils.config.attrMap.lsValues.invalidate();
      utils.config.attrMap.redundant.invalidate();
      utils.config.attrMap.capacity.invalidate();
      refresh();
      onFlash(`${r.removed} waarde(n) verwijderd, ${r.repointed} koppeling(en) omgezet${r.dropped ? `, ${r.dropped} vervallen` : ""} ✓`);
    },
    onError,
  });
  const capacity = trpc.config.attrMap.capacity.useQuery(undefined, { staleTime: 5 * 60_000 });
  const renameValue = trpc.config.attrMap.renameLsValue.useMutation({
    onSuccess: (v) => { utils.config.attrMap.lsValues.invalidate(); utils.config.attrMap.capacity.invalidate(); refresh(); setRenaming(null); onFlash(`Hernoemd naar "${v.valueTitle}" ✓`); },
    onError,
  });
  const deleteValue = trpc.config.attrMap.deleteLsValue.useMutation({
    onSuccess: (r) => {
      utils.config.attrMap.lsValues.invalidate();
      utils.config.attrMap.unmapped.invalidate();
      refresh();
      onFlash(r.mappingsRemoved ? `Waarde verwijderd, ${r.mappingsRemoved} koppeling(en) mee` : "Waarde verwijderd");
    },
    onError,
  });

  const createBulk = trpc.config.attrMap.createLsValuesBulk.useMutation({
    onSuccess: (r) => {
      utils.config.attrMap.lsValues.invalidate();
      utils.config.attrMap.unmapped.invalidate();
      refresh();
      setSelectedUnmapped([]);
      onFlash(r.failed ? `${r.created} toegevoegd, ${r.failed} mislukt` : `${r.created} waarde(n) toegevoegd en gekoppeld ✓`);
    },
    onError,
  });

  const rows = mappings.data ?? [];
  const values = lsValues.data ?? [];
  const visibleUnmapped = (unmapped.data ?? []).filter(
    (u) => (!unmappedField || u.field === unmappedField) && (!unmappedQ.trim() || `${u.field} ${u.value}`.toLowerCase().includes(unmappedQ.trim().toLowerCase())),
  );
  const visibleKeys = new Set(visibleUnmapped.map((u) => `${u.field}|${u.value}`));
  /** Ticked earlier, then filtered out of view — the reason a button can read "(12)" after five clicks. */
  const hiddenSelected = selectedUnmapped.filter((k) => !visibleKeys.has(k));
  /** Values already in the chosen group, so a near-duplicate is visible before it is created. */
  const groupValues = newValueGroup ? values.filter((v) => v.filterId === newValueGroup) : [];
  /** Lightspeed allows 15 values per filter. Enforced here rather than left to the API to refuse
   *  halfway through a batch, which would leave some values created and some not. */
  const freeInGroup = capacity.data?.groups.find((g) => g.filterId === newValueGroup)?.free ?? 0;
  const overCapacity = newValueGroup > 0 && selectedUnmapped.length > freeInGroup;

  type Suggestion = NonNullable<typeof suggest.data>[number];
  const sugKey = (s: Suggestion) => `${s.pbsField}|${s.pbsValue}|${s.ls.valueId}`;
  /** Options for one suggestion column, with counts — same shape the other column filters use. */
  const sugOptions = (pick: (s: Suggestion) => string) => {
    const counts = new Map<string, number>();
    for (const s of suggest.data ?? []) counts.set(pick(s), (counts.get(pick(s)) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([value, count]) => ({ value, label: value, count }));
  };
  const visibleSuggest = (suggest.data ?? []).filter((s) =>
    (!sugField.length || sugField.includes(s.pbsField)) &&
    (!sugValue.length || sugValue.includes(s.pbsValue)) &&
    (!sugFilter.length || sugFilter.includes(s.ls.filterTitle)) &&
    (!sugLsValue.length || sugLsValue.includes(s.ls.valueTitle)) &&
    (!sugKind.length || sugKind.includes(s.kind)));
  const sugFiltersActive = sugField.length + sugValue.length + sugFilter.length + sugLsValue.length + sugKind.length > 0;
  const groups = [...new Map(values.map((v) => [v.filterId, v.filterTitle])).entries()].map(([id, title]) => ({ id, title }));
  const valuesForDraft = draft.lsFilterId ? values.filter((v) => v.filterId === draft.lsFilterId) : values;
  const fieldValues = draft.pbsField.trim()
    ? (attrs.data?.fields ?? []).find((f) => f.field.toLowerCase() === draft.pbsField.trim().toLowerCase())?.values ?? []
    : [];

  const save = () => {
    const ls = values.find((v) => v.valueId === draft.lsValueId);
    if (!draft.pbsValue.trim() || !ls) return;
    upsert.mutate({
      id: draft.id,
      pbsField: draft.pbsField.trim() || null,
      op: draft.op,
      pbsValue: draft.pbsValue.trim(),
      lsFilterId: ls.filterId,
      lsFilterTitle: ls.filterTitle,
      lsValueId: ls.valueId,
      lsValueTitle: ls.valueTitle,
      note: draft.note.trim() || null,
    });
  };

  return (
    <div className="space-y-6">
      <Card title="Wat deze tab doet">
        <div className="space-y-2 text-xs text-slate-600">
          <p>
            De shop filtert op <b>Kleur, Formaat, Gewicht, Materiaal, Kenmerken</b> en <b>Eco Keurmerk</b>. Die vakjes worden met de hand
            aangevinkt, dus ze staan aan bij de producten waar iemand aan toe kwam en leeg bij de rest — terwijl PBS diezelfde gegevens al
            die tijd meelevert als attribuut: <span className="font-mono">Kleur=Wit</span>, <span className="font-mono">Formaat=A4</span>,{" "}
            <span className="font-mono">Ecolabel 1=FSC Mix</span>.
          </p>
          <p>
            Eén koppeling zegt: <i>dit PBS-attribuut met deze waarde betekent die Lightspeed-filterwaarde</i>. Geen slimmigheid tijdens het
            synchroniseren — het gokwerk gebeurt één keer, hieronder bij de voorstellen, waar jij het goed- of afkeurt. "Blauw" en
            "Blauw/Turquoise" zijn voor een mens dezelfde kleur en voor een tekstvergelijking niet, en dat oordeel hoor je één keer te vellen
            en vast te leggen.
          </p>
        </div>
      </Card>

      <Card
        title={`Koppelingen (${rows.length})`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setCountsOpen((v) => !v)}>{countsOpen ? "verberg aantallen" : "tel de catalogus door"}</Button>
            <Button variant="secondary" size="sm" onClick={() => setPreviewOpen((v) => !v)}>{previewOpen ? "verberg voorbeeld" : "toon voorbeeld"}</Button>
          </div>
        }
      >
        {countsOpen && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
            {counts.isLoading && <Spinner label="Hele catalogus doorrekenen…" />}
            {counts.data && (
              <>
                <div className="mb-1 text-slate-600">
                  <b>{counts.data.withAny.toLocaleString()}</b> van {counts.data.products.toLocaleString()} actieve artikelen krijgt minstens één filterwaarde.
                  {" "}Het drukste artikel krijgt er <b>{counts.data.maxOnOneProduct}</b> (Lightspeed staat er 25 toe per product).
                  {counts.data.atLimit > 0 && <span className="text-red-700"> {counts.data.atLimit.toLocaleString()} artikel(en) zitten op die grens en worden afgekapt.</span>}
                </div>
                <div className="max-h-40 space-y-0.5 overflow-auto">
                  {counts.data.perMapping.map((m) => (
                    <div key={m.id} className={m.products === 0 ? "text-slate-400" : "text-slate-600"}>
                      <span className="inline-block w-16 text-right tabular-nums">{m.products.toLocaleString()}</span> {m.label}
                      {m.products === 0 && <span className="ml-1 text-red-500">— raakt niets</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {mappings.isLoading && <Spinner label="Koppelingen laden…" />}
        {!rows.length && !mappings.isLoading && <p className="text-xs text-slate-400">Nog geen koppelingen. Neem hieronder een voorstel over, of maak er zelf een.</p>}
        {rows.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="py-1">PBS-attribuut</th><th>Voorwaarde</th><th>Lightspeed-filter</th><th>Waarde</th><th>Notitie</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((m) => {
                const stale = !values.some((v) => v.valueId === m.lsValueId);
                return (
                  <tr key={m.id} className={m.active ? undefined : "opacity-50"}>
                    <td className="py-1.5 text-xs">{m.pbsField || <span className="text-slate-400">elk attribuut</span>}</td>
                    <td className="font-mono text-xs">
                      {OPS.find((o) => o.id === m.op)?.label}{" "}
                      {m.op === "anyOf" ? m.pbsValue.split(ANY_OF_SEP).map((v) => v.trim()).join(", ") : `"${m.pbsValue}"`}
                    </td>
                    <td className="text-xs">{m.lsFilterTitle ?? m.lsFilterId}</td>
                    <td>
                      <Badge color={stale ? "red" : "blue"}>{m.lsValueTitle ?? m.lsValueId}</Badge>
                      {stale && <span className="ml-1 text-[11px] text-red-600" title="Deze filterwaarde bestaat niet meer in de shop">bestaat niet meer</span>}
                    </td>
                    <td className="text-xs text-slate-500">{m.note ?? ""}</td>
                    <td className="text-right">
                      {isAdmin && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="secondary" onClick={() => setDraft({ id: m.id, pbsField: m.pbsField ?? "", op: m.op, pbsValue: m.pbsValue, lsFilterId: m.lsFilterId, lsValueId: m.lsValueId, note: m.note ?? "" })}>wijzig</Button>
                          <Button size="sm" variant="secondary" onClick={() => upsert.mutate({ id: m.id, pbsField: m.pbsField, op: m.op, pbsValue: m.pbsValue, lsFilterId: m.lsFilterId, lsFilterTitle: m.lsFilterTitle, lsValueId: m.lsValueId, lsValueTitle: m.lsValueTitle, note: m.note, active: !m.active })}>{m.active ? "uit" : "aan"}</Button>
                          <Button size="sm" variant="danger" onClick={() => { if (confirm(`Koppeling "${m.pbsValue}" → ${m.lsValueTitle} verwijderen?`)) del.mutate({ id: m.id }); }}>verwijder</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {isAdmin && (
          <div className="mt-4 rounded-lg border border-slate-200 p-3">
            <div className="mb-2 text-xs font-medium text-slate-600">{draft.id ? `Koppeling ${draft.id} wijzigen` : "Nieuwe koppeling"}</div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={draft.pbsField} onChange={(e) => setDraft({ ...draft, pbsField: e.target.value, pbsValue: "" })} className="w-56">
                <option value="">elk attribuut</option>
                {(attrs.data?.fields ?? []).map((f) => <option key={f.field} value={f.field}>{f.field} ({f.products.toLocaleString()})</option>)}
              </Select>
              <Select value={draft.op} onChange={(e) => setDraft({ ...draft, op: e.target.value as MapOp })}>
                {OPS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </Select>
              {fieldValues.length ? (
                <Select value={draft.pbsValue} onChange={(e) => setDraft({ ...draft, pbsValue: e.target.value })} className="w-56">
                  <option value="">— kies een PBS-waarde —</option>
                  {fieldValues.map((v) => <option key={v.value} value={v.value}>{v.value} ({v.products.toLocaleString()})</option>)}
                </Select>
              ) : (
                <Input value={draft.pbsValue} onChange={(e) => setDraft({ ...draft, pbsValue: e.target.value })} placeholder="PBS-waarde" className="w-56" />
              )}
              <span className="text-slate-400">→</span>
              <Select value={String(draft.lsFilterId)} onChange={(e) => setDraft({ ...draft, lsFilterId: Number(e.target.value), lsValueId: 0 })} className="w-44">
                <option value="0">— filtergroep —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </Select>
              <Select value={String(draft.lsValueId)} onChange={(e) => setDraft({ ...draft, lsValueId: Number(e.target.value) })} className="w-48">
                <option value="0">— filterwaarde —</option>
                {valuesForDraft.map((v) => <option key={v.valueId} value={v.valueId}>{v.valueTitle}</option>)}
              </Select>
              <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="notitie" className="w-40" />
              <Button onClick={save} disabled={!draft.pbsValue.trim() || !draft.lsValueId || upsert.isPending}>{upsert.isPending ? "Opslaan…" : draft.id ? "opslaan" : "toevoegen"}</Button>
              {draft.id && <Button variant="secondary" onClick={() => setDraft(emptyDraft)}>annuleer</Button>}
            </div>
          </div>
        )}
      </Card>

      {isAdmin && (
        <Card title="Filterwaarde toevoegen in de shop">
          <p className="mb-2 text-xs text-slate-500">
            PBS levert waarden waar de shop geen vakje voor heeft — <span className="font-mono">Blaue Engel</span>,{" "}
            <span className="font-mono">PEFC Certified</span>, <span className="font-mono">Nordic Swan</span>. Hieronder staat precies wat de
            shop vandaag weggooit: elke PBS-waarde die door geen enkele koppeling wordt opgepikt, de grootste eerst. Vink aan wat je wilt,
            kies de filtergroep, en ze worden aangemaakt <b>én meteen gekoppeld</b> — een waarde aanmaken en de koppeling vergeten laat het
            vakje voor altijd leeg, wat er precies uitziet als een storing.
          </p>
          <p className="mb-2 text-xs text-amber-700">
            Dit schrijft direct naar Lightspeed en is zichtbaar voor klanten. Dubbele waarden worden geweigerd, en er wordt niets opnieuw
            geprobeerd bij een storing — een herhaalde poging zou een dubbel vakje in de winkel achterlaten.
          </p>

          {/* Lightspeed allows 15 values per filter and 6 filters, full stop. Showing the room left is
              not a nicety: Kleur is already at 15, so "add these 37 colours" was never possible. */}
          {capacity.data && (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs">
              <div className="mb-1 text-slate-600">
                Lightspeed staat <b>{capacity.data.maxValuesPerFilter} waarden per filter</b> toe, <b>{capacity.data.maxFilters} filters</b> per shop en{" "}
                <b>{capacity.data.maxValuesPerProduct} filterwaarden per product</b>. Vol is vol — dan is toewijzen aan een bestaand vakje de enige weg.
              </div>
              <div className="flex flex-wrap gap-2">
                {capacity.data.groups.map((g) => (
                  <span key={g.filterId} className={`rounded-full px-2 py-0.5 ${g.free === 0 ? "bg-red-100 text-red-800" : g.free <= 2 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}>
                    {g.filterTitle} {g.used}/{capacity.data!.maxValuesPerFilter}{g.free === 0 ? " — vol" : ` — ${g.free} vrij`}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Select value={unmappedField} onChange={(e) => setUnmappedField(e.target.value)} className="w-56">
              <option value="">alle attributen</option>
              {[...new Set((unmapped.data ?? []).map((u) => u.field))].map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
            <Input value={unmappedQ} onChange={(e) => setUnmappedQ(e.target.value)} placeholder="zoek een waarde…" className="w-56" />
            {selectedUnmapped.length > 0 && (
              <button onClick={() => setSelectedUnmapped([])} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200">
                {selectedUnmapped.length} geselecteerd — wissen
              </button>
            )}
            {hiddenSelected.length > 0 && (
              <button
                onClick={() => setSelectedUnmapped(selectedUnmapped.filter((k) => visibleKeys.has(k)))}
                className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
                title="Deze staan nog aangevinkt maar vallen buiten je huidige filter — klik om ze te laten vallen"
              >
                {hiddenSelected.length} buiten dit filter — laten vallen
              </button>
            )}
          </div>

          {/* Selections survive a filter change on purpose (you build a batch across attributes), but a
              tick you cannot see is a tick you did not mean. So they are shown here, individually removable. */}
          {hiddenSelected.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1 rounded-lg border border-amber-200 bg-amber-50 p-2">
              <span className="mr-1 text-[11px] font-medium text-amber-800">Ook geselecteerd, nu verborgen:</span>
              {hiddenSelected.map((k) => {
                const [field, ...rest] = k.split("|");
                return (
                  <button
                    key={k}
                    onClick={() => setSelectedUnmapped(selectedUnmapped.filter((x) => x !== k))}
                    className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-600 ring-1 ring-amber-200 hover:bg-red-50 hover:text-red-700"
                    title={`${field} — klik om te verwijderen uit de selectie`}
                  >
                    {rest.join("|")} ✕
                  </button>
                );
              })}
            </div>
          )}

          {unmapped.isLoading && <Spinner label="Ongekoppelde waarden zoeken…" />}
          {unmapped.data && !visibleUnmapped.length && <p className="text-xs text-slate-400">Niets ongekoppeld dat aan dit filter voldoet.</p>}
          <div className="mb-3 max-h-60 overflow-auto rounded-lg border border-slate-200">
            {visibleUnmapped.map((u) => {
              const key = `${u.field}|${u.value}`;
              const on = selectedUnmapped.includes(key);
              return (
                <label key={key} className={`flex cursor-pointer items-center gap-2 border-b border-slate-50 px-2 py-1 text-xs last:border-0 ${on ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => setSelectedUnmapped(on ? selectedUnmapped.filter((k) => k !== key) : [...selectedUnmapped, key])}
                    className="accent-indigo-600"
                  />
                  <span className="w-44 truncate text-slate-500" title={u.field}>{u.field}</span>
                  <span className="flex-1 truncate font-medium text-slate-700">{u.value}</span>
                  <span className="w-16 text-right tabular-nums text-slate-400">{u.products.toLocaleString()}</span>
                </label>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(newValueGroup)} onChange={(e) => setNewValueGroup(Number(e.target.value))} className="w-56">
              <option value="0">— filtergroep —</option>
              {groups.map((g) => {
                const cap = capacity.data?.groups.find((c) => c.filterId === g.id);
                return <option key={g.id} value={g.id} disabled={cap?.free === 0}>{g.title}{cap ? ` — ${cap.free} vrij` : ""}{cap?.free === 0 ? " (vol)" : ""}</option>;
              })}
            </Select>
            <Button
              disabled={!newValueGroup || !selectedUnmapped.length || createBulk.isPending || overCapacity}
              onClick={() => {
                const group = groups.find((g) => g.id === newValueGroup)?.title ?? "";
                const items = selectedUnmapped.map((k) => {
                  const [field, ...rest] = k.split("|");
                  const value = rest.join("|");
                  return { title: value, pbsField: field || null, pbsValue: value };
                });
                if (confirm(`${items.length} waarde(n) toevoegen aan "${group}" in de webshop en koppelen aan PBS?\n\n${items.map((i) => `• ${i.title}`).join("\n")}\n\nDit is direct zichtbaar voor klanten.`)) {
                  createBulk.mutate({ filterId: newValueGroup, items });
                }
              }}
            >
              {createBulk.isPending ? "Toevoegen…" : `toevoegen in Lightspeed${selectedUnmapped.length ? ` (${selectedUnmapped.length})` : ""}`}
            </Button>
            {newValueGroup > 0 && (
              <span className={`text-xs ${overCapacity ? "font-medium text-red-700" : "text-slate-500"}`}>
                {overCapacity
                  ? `${selectedUnmapped.length} geselecteerd, maar er is nog maar ruimte voor ${freeInGroup} — Lightspeed staat 15 waarden per filter toe. Vink er ${selectedUnmapped.length - freeInGroup} af, of wijs ze toe aan een bestaand vakje.`
                  : `nog ${freeInGroup} plek(ken) vrij in deze groep`}
              </span>
            )}
            <span className="text-slate-300">of</span>
            <Input value={newValueTitle} onChange={(e) => setNewValueTitle(e.target.value)} placeholder="eigen naam" className="w-48" />
            <Button
              variant="secondary"
              disabled={!newValueGroup || !newValueTitle.trim() || createValue.isPending}
              onClick={() => { if (confirm(`"${newValueTitle.trim()}" toevoegen aan de filtergroep in de webshop? Dit is direct zichtbaar voor klanten.`)) createValue.mutate({ filterId: newValueGroup, title: newValueTitle.trim(), mapFrom: newValueFrom ? { pbsField: newValueFrom.field, pbsValue: newValueFrom.value } : undefined }); }}
            >
              {createValue.isPending ? "Toevoegen…" : "los toevoegen"}
            </Button>
            {createValue.isError && <span className="text-xs text-red-600">{createValue.error.message}</span>}
          </div>

          {/* What the group already contains, so "90 g" is not added next to the "90gr" that has been
              there since 2015. The server refuses an obvious twin, but seeing it beats being refused. */}
          {groupValues.length > 0 && (
            <div className="mt-2 text-[11px] text-slate-500">
              <span className="font-medium">Staat al in {groups.find((g) => g.id === newValueGroup)?.title}:</span>{" "}
              {groupValues.map((v) => v.valueTitle).join(" · ")}
            </div>
          )}

          {/* Results, not just a toast: these are live shop writes and you should be able to read back
              exactly what happened to each one. */}
          {createBulk.data && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs">
              <div className="mb-1 font-medium text-slate-700">
                Resultaat: {createBulk.data.created} aangemaakt en gekoppeld
                {createBulk.data.failed > 0 && <span className="text-red-700">, {createBulk.data.failed} mislukt</span>}
              </div>
              <div className="max-h-40 space-y-0.5 overflow-auto">
                {createBulk.data.results.map((r) => (
                  <div key={r.title} className={r.ok ? "text-emerald-700" : "text-red-700"}>
                    {r.ok ? "✓" : "✕"} {r.title}
                    {r.ok ? <span className="text-slate-400"> — waarde {r.valueId}, koppeling gelegd</span> : <span> — {r.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {isAdmin && (
        <Card
          title="Filterwaarden in de shop"
          actions={
            <div className="flex items-center gap-2">
              {(redundant.data?.length ?? 0) > 0 && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={cleanup.isPending}
                  onClick={() => {
                    const plan = redundant.data ?? [];
                    const lines = plan.map((p) => `• ${p.remove.filterTitle} / "${p.remove.valueTitle}" ${p.keep ? `(dubbel met "${p.keep.valueTitle}")` : "(geen echte waarde)"}`).join("\n");
                    const repoints = plan.flatMap((p) => p.mappings.filter((m) => m.repoint != null)).length;
                    if (confirm(`${plan.length} filterwaarde(n) verwijderen uit de webshop?\n\n${lines}\n\n${repoints} koppeling(en) worden eerst omgezet naar de waarde die blijft. Dit is direct zichtbaar voor klanten en onomkeerbaar.`)) cleanup.mutate();
                  }}
                >
                  {cleanup.isPending ? "Opruimen…" : `${redundant.data!.length} overbodig — opruimen`}
                </Button>
              )}
              <Select value={String(shopGroup)} onChange={(e) => setShopGroup(Number(e.target.value))} className="w-44">
                <option value="0">— alle groepen —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </Select>
            </div>
          }
        >
          <p className="mb-2 text-xs text-slate-500">
            Wat er nu in de webshop staat, per filtergroep. Waarden die op elkaar lijken staan naast elkaar gemarkeerd — zo zie je dat{" "}
            <span className="font-mono">90 g</span> en <span className="font-mono">90gr</span> hetzelfde vakje zijn. Verwijderen haalt de
            waarde uit de shop én de koppeling eruit; dat is direct zichtbaar voor klanten en kan niet ongedaan gemaakt worden.
          </p>
          {cleanup.data && (
            <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs">
              <div className="font-medium text-slate-700">Opgeruimd: {cleanup.data.removed} verwijderd, {cleanup.data.repointed} koppeling(en) omgezet{cleanup.data.dropped ? `, ${cleanup.data.dropped} vervallen` : ""}</div>
              <div className="max-h-40 overflow-auto">{cleanup.data.details.map((d) => <div key={d} className="text-slate-600">{d}</div>)}</div>
            </div>
          )}
          {lsValues.isLoading && <Spinner label="Filterwaarden laden…" />}
          <div className="max-h-72 overflow-auto">
            {(shopGroup ? values.filter((v) => v.filterId === shopGroup) : values).map((v) => {
              const twins = values.filter((o) => o.filterId === v.filterId && o.valueId !== v.valueId && looksSame(o.valueTitle, v.valueTitle));
              const mapped = rows.filter((m) => m.lsValueId === v.valueId);
              return (
                <div key={v.valueId} className={`flex items-center gap-2 border-b border-slate-50 py-1 text-xs ${twins.length ? "bg-red-50" : ""}`}>
                  <span className="w-32 truncate text-slate-500">{v.filterTitle}</span>
                  {renaming?.valueId === v.valueId ? (
                    <>
                      <Input value={renaming.title} onChange={(e) => setRenaming({ ...renaming, title: e.target.value })} className="w-56 text-xs" autoFocus />
                      <Button size="sm" disabled={!renaming.title.trim() || renameValue.isPending} onClick={() => renameValue.mutate({ filterId: v.filterId, valueId: v.valueId, title: renaming.title.trim() })}>opslaan</Button>
                      <Button size="sm" variant="secondary" onClick={() => setRenaming(null)}>annuleer</Button>
                    </>
                  ) : (
                    <>
                      <span className="w-56 truncate font-medium text-slate-700">{v.valueTitle}</span>
                      <Button size="sm" variant="secondary" onClick={() => setRenaming({ valueId: v.valueId, title: v.valueTitle })}>hernoem</Button>
                    </>
                  )}
                  {twins.length > 0 && <span className="text-red-700" title="Lijkt op een andere waarde in dezelfde groep">≈ {twins.map((t) => t.valueTitle).join(", ")}</span>}
                  <span className="flex-1 truncate text-slate-400">{mapped.length ? `${mapped.length} koppeling(en): ${mapped.map((m) => `${m.pbsField} = ${m.pbsValue}`).join(", ")}` : "geen koppeling"}</span>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={deleteValue.isPending}
                    onClick={() => { if (confirm(`"${v.valueTitle}" verwijderen uit de filtergroep ${v.filterTitle} in de webshop?\n\n${mapped.length ? `Ook ${mapped.length} koppeling(en) worden verwijderd.\n\n` : ""}Dit is direct zichtbaar voor klanten en kan niet ongedaan gemaakt worden.`)) deleteValue.mutate({ filterId: v.filterId, valueId: v.valueId }); }}
                  >
                    verwijder
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card title="Specificatieblok testen op één artikel">
        <p className="mb-2 text-xs text-slate-500">
          PBS levert 209 attribuutvelden; zes filters van vijftien waarden kunnen er negentig kwijt. De rest — Schrijfbreedte, Aantal
          bladzijden, Liniëring, Binding, Ringen, Vervaardigd uit — kan nooit een filtervakje worden en bereikt de shop nu helemaal niet.
          Dit blok zet ze onder de lange productomschrijving. Dat kost <b>geen extra API-aanroepen</b>, want de sync schrijft die tekst toch
          al, en het werkt in elk thema — anders dan de Specificaties-tab van Lightspeed.
        </p>
        <p className="mb-2 text-xs text-slate-500">
          Alle attributen gaan erin, ook die al een filtervakje hebben: een specificatielijst is het gegevensblad van het product, een filter
          is een manier om er te komen. Wie de specificaties van een vuilniszak leest verwacht te zien dat hij zwart is. Het blok komt
          onderaan het <b>INHOUD</b>-veld; de korte <b>Beschrijving</b> blijft ongemoeid, en bestaande tekst en opmaak blijven staan.
        </p>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Input value={specCode} onChange={(e) => setSpecCode(e.target.value)} placeholder="PBS-productcode, bijv. 052077" className="w-64" />
          {isAdmin && (
            <Button
              disabled={!specPreview.data?.block || !specPreview.data?.lsProductId || specApply.isPending}
              onClick={() => { if (confirm(`De omschrijving van ${specPreview.data!.productCode} in de webshop bijwerken?\n\nDit is één product, direct zichtbaar voor klanten.`)) specApply.mutate({ productCode: specCode.trim() }); }}
            >
              {specApply.isPending ? "Schrijven…" : "op dit ene product zetten"}
            </Button>
          )}
          {specApply.isError && <span className="text-xs text-red-600">{specApply.error.message}</span>}
          {specApply.data && <span className="text-xs text-emerald-700">geschreven naar product {specApply.data.productId} ✓</span>}
        </div>
        {specPreview.isFetching && <Spinner label="Voorbeeld maken…" />}
        {specCode.trim().length > 2 && specPreview.data === null && !specPreview.isFetching && <p className="text-xs text-red-600">Geen PBS-artikel met die code.</p>}
        {specPreview.data && (
          <div className="space-y-2 text-xs">
            <div className="text-slate-600">
              <b>{specPreview.data.title}</b> — {specPreview.data.attributes} attribuut/attributen
              {!specPreview.data.lsProductId && <span className="ml-1 text-amber-700">(staat niet op Lightspeed — niets om bij te werken)</span>}
            </div>
            {!specPreview.data.block && <p className="text-slate-400">Alle attributen van dit artikel zijn al gedekt door een koppeling — geen blok nodig.</p>}
            {specPreview.data.block && (
              <>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">zo komt het eruit te zien</div>
                  <div className="max-w-none text-slate-700 [&_h3]:mb-1 [&_h3]:font-semibold [&_li]:list-disc [&_ul]:ml-5" dangerouslySetInnerHTML={{ __html: specPreview.data.block }} />
                </div>
                <details className="text-slate-500">
                  <summary className="cursor-pointer">de HTML die geschreven wordt</summary>
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px]">{specPreview.data.newContent}</pre>
                </details>
              </>
            )}
          </div>
        )}
      </Card>

      <Card
        title={`Voorstellen (${visibleSuggest.length}${visibleSuggest.length !== (suggest.data?.length ?? 0) ? ` van ${suggest.data?.length}` : ""})`}
        actions={
          isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              {sugFiltersActive && (
                <button onClick={() => { setSugField([]); setSugValue([]); setSugFilter([]); setSugLsValue([]); setSugKind([]); }} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
                  filters wissen
                </button>
              )}
              <Button size="sm" variant="secondary" onClick={() => setPickedSuggest(visibleSuggest.map(sugKey))} disabled={!visibleSuggest.length}>
                selecteer alle {visibleSuggest.length} zichtbare
              </Button>
              {pickedSuggest.length > 0 && <Button size="sm" variant="secondary" onClick={() => setPickedSuggest([])}>selectie wissen</Button>}
              <Button
                size="sm"
                disabled={!pickedSuggest.length || acceptBulk.isPending}
                onClick={() => {
                  const items = (suggest.data ?? []).filter((s) => pickedSuggest.includes(sugKey(s))).map((s) => ({
                    pbsField: s.pbsField, op: "equals" as const, pbsValue: s.pbsValue,
                    lsFilterId: s.ls.filterId, lsFilterTitle: s.ls.filterTitle, lsValueId: s.ls.valueId, lsValueTitle: s.ls.valueTitle,
                  }));
                  if (confirm(`${items.length} voorstel(len) overnemen als koppeling?`)) acceptBulk.mutate({ items });
                }}
              >
                {acceptBulk.isPending ? "Overnemen…" : `overnemen (${pickedSuggest.length})`}
              </Button>
            </div>
          )
        }
      >
        <p className="mb-2 text-xs text-slate-500">
          PBS-waarden die letterlijk overeenkomen met een filterwaarde in de shop en nog niet gekoppeld zijn. Alleen exacte overeenkomsten —
          iets slimmers levert voorstellen op die niemand kan controleren, en deze lijst bestaat om gecontroleerd te worden. Filter per kolom,
          vink aan, en neem ze in één keer over.
        </p>
        {suggest.isLoading && <Spinner label="Voorstellen zoeken…" />}
        {suggest.data && !suggest.data.length && <p className="text-xs text-slate-400">Niets nieuws — alles wat letterlijk overeenkomt is al gekoppeld.</p>}
        {acceptBulk.data && (
          <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs">
            <div className="font-medium text-slate-700">
              {acceptBulk.data.created} koppeling(en) gemaakt{acceptBulk.data.failed > 0 && <span className="text-red-700">, {acceptBulk.data.failed} mislukt</span>}
            </div>
            {acceptBulk.data.failed > 0 && (
              <div className="max-h-32 overflow-auto">
                {acceptBulk.data.results.filter((r) => !r.ok).map((r) => <div key={r.label} className="text-red-700">✕ {r.label} — {r.error}</div>)}
              </div>
            )}
          </div>
        )}
        {suggest.data && suggest.data.length > 0 && (
          <table className="w-full text-left text-xs">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-6"></th>
                <th className="py-1"><ColumnFilter title="PBS-attribuut" selected={sugField} onChange={setSugField} options={sugOptions((s) => s.pbsField)} /></th>
                <th><ColumnFilter title="PBS-waarde" selected={sugValue} onChange={setSugValue} options={sugOptions((s) => s.pbsValue)} /></th>
                <th className="text-right font-normal normal-case tracking-normal text-slate-400">artikelen</th>
                <th><ColumnFilter title="LS-filter" selected={sugFilter} onChange={setSugFilter} options={sugOptions((s) => s.ls.filterTitle)} /></th>
                <th><ColumnFilter title="LS-waarde" selected={sugLsValue} onChange={setSugLsValue} options={sugOptions((s) => s.ls.valueTitle)} /></th>
                <th><ColumnFilter title="Soort" selected={sugKind} onChange={setSugKind} options={sugOptions((s) => s.kind)} /></th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visibleSuggest.map((s) => {
                const k = sugKey(s);
                const on = pickedSuggest.includes(k);
                return (
                  <tr key={k} className={on ? "bg-indigo-50" : undefined}>
                    <td className="py-1">
                      <input type="checkbox" checked={on} onChange={() => setPickedSuggest(on ? pickedSuggest.filter((x) => x !== k) : [...pickedSuggest, k])} className="accent-indigo-600" />
                    </td>
                    <td className="max-w-[10rem] truncate text-slate-500" title={s.pbsField}>{s.pbsField}</td>
                    <td className="max-w-[12rem] truncate font-medium text-slate-700">{s.pbsValue}</td>
                    <td className="text-right tabular-nums text-slate-400">{s.products.toLocaleString()}</td>
                    <td className="text-slate-500">{s.ls.filterTitle}</td>
                    <td><Badge color="blue">{s.ls.valueTitle}</Badge></td>
                    <td title={s.why}>
                      <Badge color={s.kind === "exact" || s.kind === "format" ? "green" : s.kind === "band" ? "blue" : s.kind === "contains" || s.kind === "synonym" ? "amber" : s.kind === "combo" ? "indigo" : "gray"}>
                        {KIND_LABEL[s.kind]}
                      </Badge>
                    </td>
                    <td className="text-right">
                      {isAdmin && (
                        <Button size="sm" variant="secondary" onClick={() => upsert.mutate({ pbsField: s.pbsField, op: "equals", pbsValue: s.pbsValue, lsFilterId: s.ls.filterId, lsFilterTitle: s.ls.filterTitle, lsValueId: s.ls.valueId, lsValueTitle: s.ls.valueTitle })}>
                          overnemen
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {previewOpen && (
        <Card title="Voorbeeld: wat krijgt welk filter">
          {preview.isLoading && <Spinner label="Koppelingen toepassen…" />}
          {preview.data && (
            <>
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Code</th><th>Omschrijving</th><th>Filterwaarden</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.data.rows.map((r) => (
                    <tr key={r.productCode}>
                      <td className="py-1.5 font-mono text-xs">{r.productCode}</td>
                      <td className="max-w-md truncate text-xs text-slate-600" title={r.title}>{r.title}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {r.values.map((v) => <Badge key={`${v.filterTitle}${v.valueTitle}`} color="blue"><span title={`via ${v.via}`}>{v.filterTitle}: {v.valueTitle}</span></Badge>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!preview.data.rows.length && <p className="py-3 text-xs text-slate-400">Geen artikel op deze pagina krijgt een filterwaarde.</p>}
              <div className="mt-3 flex items-center gap-2">
                <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>vorige</Button>
                <span className="text-xs text-slate-500">pagina {page + 1}</span>
                <Button variant="secondary" size="sm" disabled={preview.data.rows.length < 100} onClick={() => setPage((p) => p + 1)}>volgende</Button>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
